import { AreaSalesIncomes, PriceInvoiceInterface } from '@/interfaces/ServerInterfaces';
import { StyleSheet, Text, View } from '@react-pdf/renderer';

interface SelledReportTableInterface {
    tableName?: string
    containTotals?: boolean
    displayTableTitles?: boolean,
    firstColumnLarger?: boolean,
    resp: AreaSalesIncomes
}

export const SelledReportTable = ({
                                      tableName,
                                      containTotals,
                                      displayTableTitles,
                                      firstColumnLarger = true,
                                      resp
                                  }: SelledReportTableInterface) => {

    //Styles
    const tableStyles = StyleSheet.create({
        tableTitle: {
            fontSize: 13,
            fontWeight: 'bold',
            marginBottom: 10,
        },
        row: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: 'solid',
            borderBottomColor: '#7a7a7a',
            borderBottomWidth: 1,
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
        },
        rowTitle :{
            fontSize: 13,
            fontWeight: 'semibold',
        },
        firstRowText: {
            fontSize: 12,
            fontWeight: 'bold',
            width: '100%',
            textAlign: 'left',
        },
        middleRowText: {
            fontSize: 12,
            textAlign: 'center'
        },
        lastRowText: {
            fontSize: 12,
            textAlign: 'right',
        },
        container: {
            width: '100%',
            marginTop: 10,
            marginBottom: 40
        }
    })
    const rows: {
        column1: string
        value:  {
            c2: string
            c3: string[]
        }[],
        rowStyle?: any
    }[] = []

    rows.push({
        column1: 'Total en ventas',
        value: [{
            c2: '',
            c3: resp.totalSales.map((item) => `${item.amount} ${item.codeCurrency}`),
        }]
    })
    const totalIngresosPorMoneda = [...resp.totalIncomesNotInCash, ...resp.totalIncomesInCash].reduce((acc: PriceInvoiceInterface[], item) => {
        const existing = acc.find((entry) => entry.codeCurrency === item.codeCurrency);
        if (existing) {
            existing.amount += item.amount;
        } else {
            acc.push({ ...item });
        }
        return acc;
    }, []);
    rows.push({
        column1: 'Total en ingresos',
        value: [{
            c2: '',
            c3: totalIngresosPorMoneda.map((item) => `${item.amount} ${item.codeCurrency}`),
        }]
    })
    rows.push({
        column1: 'Ingresos por formas de pago',
        value:  [
            {
                c2: 'Efectivo',
                c3: resp.totalIncomesInCash.map((item) => `${item.amount} ${item.codeCurrency}`),
            },
            {
                c2: 'Transferencia',
                c3: resp.totalIncomesNotInCash.map((item) => `${item.amount} ${item.codeCurrency}`),
            }
        ]
    })
    rows.push({
        column1: 'Operaciones de caja',
        value:  [
            {
                c2: 'Fondo',
                c3: resp.totalCashOperations
                    .filter((itm: { operation: string }) => itm.operation === 'MANUAL_FUND')
                    .map((item) => `${item.amount} ${item.codeCurrency}`),
            },
            {
                c2: 'Extracciones',
                c3: resp.totalCashOperations
                    .filter((itm: { operation: string }) => itm.operation === 'MANUAL_WITHDRAW')
                    .map((item) => `${item.amount} ${item.codeCurrency}`),
            },
            {
                c2: 'Depósitos',
                c3: resp.totalCashOperations
                    .filter((itm: { operation: string }) => itm.operation === 'MANUAL_DEPOSIT')
                    .map((item) => `${item.amount} ${item.codeCurrency}`),
            }
        ]
    })

    rows.push({
        column1: 'Propinas',
        value:  [
            {
                c2: '',
                c3: [`${resp.totalTipsMainCurrency.amount} ${resp.totalTipsMainCurrency.codeCurrency}`],
            },
        ],
        rowStyle: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
        },
    })
    rows.push({
        column1: 'Envíos',
        value:  [
            {
                c2: '',
                c3: resp.totalShipping.map((item) => `${item.amount} ${item.codeCurrency}`),
            },
        ],
        rowStyle: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
        },
    })
    rows.push({
        column1: 'Descuentos',
        value:  [
            {
                c2: '',
                c3: resp.totalDiscounts.map((item) => `${item.amount} ${item.codeCurrency}`),
            },
        ],
        rowStyle: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
        },
    })
    rows.push({
        column1: 'Comisiones',
        value:  [
            {
                c2: '',
                c3: resp.totalCommissions.map((item) => `${item.amount} ${item.codeCurrency}`),
            },
        ],
        rowStyle: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
        },
    })
    rows.push({
        column1: 'Consumo casa',
        value:  [
            {
                c2: '',
                c3: resp.totalHouseCosted.map((item) => `${item.amount} ${item.codeCurrency}`),
            },
        ],
    })
    rows.push({
        column1: 'Efectivo en caja',
        value:  [
            {
                c2: '',
                c3: resp.totalInCash.map((item) => `${item.amount} ${item.codeCurrency}`),
            },
        ],
    })
    rows.push({
        column1: 'Costo de las mercancías',
        value:  [
            {
                c2: '',
                c3: [`${resp.totalCost.amount} ${resp.totalCost.codeCurrency}`],
            },
        ],
        rowStyle: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
        },
    })
    rows.push({
        column1: 'Ganancia en ventas',
        value:  [
            {
                c2: '',
                c3: [`${resp.totalGrossRevenue.amount} ${resp.totalGrossRevenue.codeCurrency}`],
            },
        ],
    })



    return (
        <>
            <View style={tableStyles.container}>
                <Text style={tableStyles.tableTitle}>{tableName}</Text>
                {rows.map((row) => (
                    <View style={row.rowStyle ? row.rowStyle : tableStyles.row}>
                        <Text style={tableStyles.firstRowText}>{row.column1}</Text>
                        <View
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-around',
                                width: '100%',
                                alignItems: 'center',
                                rowGap: 10,
                            }}
                        >
                            {row.value.map((item, idx) => (
                                <View
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Text style={tableStyles.middleRowText}>{item.c2}</Text>
                                    <View style={
                                        {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-around',
                                            width: '100%',
                                            alignItems: "flex-end"
                                        }
                                    }>
                                        {item.c3.length > 0 ? (
                                            item.c3.map((subItem, subIdx) => (
                                                <Text style={tableStyles.lastRowText} key={subIdx}>
                                                    {subItem}
                                                </Text>
                                            ))
                                        ) : (
                                            <Text style={tableStyles.lastRowText}>--</Text>
                                        )}
                                    </View>
                                </View>
                            ))}
                        </View>
                    </View>
                ))}


            </View>


        </>
    )
}